"use client";
import { useState } from "react";
import { Heart, Loader2, Check, Share2, X } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/app/components/shadcn/button";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useCart from "@/app/lib/features/cart/cart.hook";
import useFavorite from "@/app/lib/features/favorite/favorite.hook";
import useModal from "@/app/lib/features/modal/modal.hook";
import { trackProductAddToCart, trackProductFavorite, trackInteraction } from "@/app/lib/tracking/interactions";

const ProductMobileBar = () => {
  const router = useRouter();
  const { product, selectedColorIndex, selectedSizeIndex, updateVariantFavorite } =
    useProductDetail();
  const { addToCart } = useCart();
  const { toggleFavorite, isToggling } = useFavorite();
  const { createModal } = useModal();

  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartAdded, setCartAdded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  
  if (!product) return null;

  const productId = product.id;

  const selectedColor = product.variants_by_color?.[selectedColorIndex];
  // Favori durumu doğrudan API verisinden: seçili rengin is_favorited'ı.
  const favoriteVariantId = selectedColor?.variant_id ?? product.variant_id;
  const isFav = selectedColor?.is_favorited ?? product.is_favorited ?? false;
  const isTogglingFav = isToggling(productId);

  const selectedSize = selectedSizeIndex !== -1 ? selectedColor?.sizes?.[selectedSizeIndex] : undefined;
  const isOutOfStock = selectedSize?.stock === 0;
  const hasAnyStock = selectedColor?.sizes?.some((s) => s.stock > 0) ?? false;

  const execAddToCart = async (colorIdx: number, sizeIdx: number) => {
    const color = product.variants_by_color?.[colorIdx];
    const size = color?.sizes?.[sizeIdx];
    if (!size) return;
    setIsAddingToCart(true);
    const result = await addToCart(
      { product_id: productId, variant_id: size.id, quantity: 1 },
      false,
      { name: product.name, price: size.price, brand: product.brand_name ?? undefined, affiliation: product.seller?.name, category: product.category?.name },
    );
    if (result.success) {
      trackProductAddToCart(productId);
      createModal("added-to-cart", {
        image: color?.primary_image ?? product.images?.[0] ?? "",
        name: product.internet_product_description || product.name,
        colorName: color?.color_name,
        sizeName: size.size,
        price: `${(result.price ?? size.price).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
      });
      setCartAdded(true);
      setTimeout(() => { setCartAdded(false); setIsAddingToCart(false); }, 2000);
    } else {
      toast.error(result.message ?? "Sepete eklenirken bir hata oluştu");
      setIsAddingToCart(false);
    }
  };

  const execBuyNow = async (colorIdx: number, sizeIdx: number) => {
    const color = product.variants_by_color?.[colorIdx];
    const size = color?.sizes?.[sizeIdx];
    if (!size) return;
    const result = await addToCart(
      { product_id: productId, variant_id: size.id, quantity: 1 },
      false,
      { name: product.name, price: size.price, brand: product.brand_name ?? undefined, affiliation: product.seller?.name, category: product.category?.name },
    );
    if (result.success) router.push("/sepet");
    else toast.error(result.message ?? "Sepete eklenirken bir hata oluştu");
  };

  const openSizePicker = (action: "addToCart" | "buyNow") => {
    createModal("size-picker", {
      initialColorIndex: selectedColorIndex,
      initialSizeIndex: selectedSizeIndex,
      onConfirm: (colorIdx: number, sizeIdx: number) => {
        if (action === "addToCart") execAddToCart(colorIdx, sizeIdx);
        else execBuyNow(colorIdx, sizeIdx);
      },
    });
  };

  const handleBuyNow = () => {
    if (selectedSizeIndex === -1) { openSizePicker("buyNow"); return; }
    execBuyNow(selectedColorIndex, selectedSizeIndex);
  };

  const handleAddToCart = () => {
    if (selectedSizeIndex === -1) { openSizePicker("addToCart"); return; }
    execAddToCart(selectedColorIndex, selectedSizeIndex);
  };

  const handleToggleFavorite = async () => {
    const success = await toggleFavorite(productId, favoriteVariantId);
    if (success) {
      updateVariantFavorite(favoriteVariantId, !isFav);
      if (!isFav) trackProductFavorite(productId);
      toast.success(isFav ? "Favorilerden çıkarıldı" : "Favorilere eklendi");
    } else toast.error("Bir hata oluştu");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      trackInteraction("product", productId, "click");
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const shareLinks = product.share?.dropdown.items.map((item) => {
    const getAction = () => {
      if (item.type === "copy") return handleCopyLink;
      return () => {
        const encodedUrl = encodeURIComponent(window.location.href);
        const urlMap: Record<string, string> = {
          whatsapp: `https://wa.me/?text=${encodedUrl}`,
          telegram: `https://t.me/share/url?url=${encodedUrl}`,
          facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
          x: `https://x.com/intent/tweet?url=${encodedUrl}`,
          email: `mailto:?subject=Bu ürünü kontrol et!&body=${encodedUrl}`,
        };
        trackInteraction("product", productId, "click");
        window.open(urlMap[item.url] || item.url, "_blank");
      };
    };
    return {
      name: item.type === "copy" && isCopied ? "Kopyalandı!" : item.title,
      iconUrl: item.icon,
      action: getAction(),
      isCopyAction: item.type === "copy",
    };
  }) ?? [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 sm:hidden"
        style={{
          opacity: shareOpen ? 1 : 0,
          pointerEvents: shareOpen ? "auto" : "none",
          transition: "opacity 0.2s ease",
        }}
        onClick={() => setShareOpen(false)}
      />

      {/* Share Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-xl sm:hidden"
        style={{
          transform: shareOpen ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.38s cubic-bezier(0.2, 0, 0, 1)",
        }}
      >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="font-semibold text-sm">{product.share?.dropdown.text ?? "Paylaş"}</span>
              <button onClick={() => setShareOpen(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-2 pb-safe">
              {shareLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => { link.action(); if (!link.isCopyAction) setShareOpen(false); }}
                  className="cursor-pointer w-full flex items-center gap-3 px-4 py-3 text-sm rounded-xl hover:bg-gray-100 transition-colors text-left"
                >
                  {link.isCopyAction && isCopied ? (
                    <Check size={18} className="text-green-600" />
                  ) : link.iconUrl ? (
                    <Image src={link.iconUrl} alt={link.name} width={18} height={18} className="w-5 h-5 object-contain" />
                  ) : (
                    <Share2 size={18} className="text-gray-600" />
                  )}
                  <span>{link.name}</span>
                </button>
              ))}
            </div>
          </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t sm:hidden">
        {/* Price Row */}
        <div className="flex items-center gap-2 px-3 pt-2">
          {product.has_discount && product.list_price && product.list_price > product.price && (
            <span className="text-xs text-muted-foreground line-through">
              {product.list_price.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {product.currency_symbol}
            </span>
          )}
          {product.discount_percentage > 0 && (
            <span
              className="px-1.5 py-0.5 rounded text-xs font-bold"
              style={{ backgroundColor: product.discount_background_color || "#ff9500ff", color: product.discount_text_color || "#FFF" }}
            >
              %{product.discount_percentage} İndirim
            </span>
          )}
          <span className="text-lg font-bold text-main">
            {product.price.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {product.currency_symbol}
          </span>
        </div>
        <div className="flex gap-2 px-3 py-2">
          <Button
            type="button"
            variant="outline-main"
            size="lg"
            className="border-2 flex-1"
            onClick={handleBuyNow}
            disabled={isOutOfStock || !hasAnyStock}
          >
            Şimdi Al
          </Button>
          <Button
            type="button"
            size="lg"
            variant="orange"
            className="flex-1"
            onClick={handleAddToCart}
            disabled={isAddingToCart || isOutOfStock || !hasAnyStock}
          >
            {isAddingToCart ? (
              cartAdded ? <><Check className="mr-1 h-4 w-4" />Eklendi</> : <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Ekleniyor</>
            ) : "Sepete Ekle"}
          </Button>
          <Button
            type="button"
            size="icon-lg"
            variant={isFav ? "default" : "outline"}
            className={`rounded-full shrink-0 ${isFav ? "bg-red-500 hover:bg-red-600 border-red-500" : ""}`}
            onClick={handleToggleFavorite}
            disabled={isTogglingFav}
          >
            {isTogglingFav ? <Loader2 className="animate-spin" /> : <Heart className={isFav ? "fill-current" : ""} />}
          </Button>
          {product.share && (
            <Button
              type="button"
              size="icon-lg"
              variant="outline"
              className="rounded-full shrink-0"
              onClick={() => setShareOpen(true)}
            >
              <Share2 />
            </Button>
          )}
        </div>
      </div>

      </>
  );
};

export default ProductMobileBar;